import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import EventBus from 'eventing-bus';
import { verify2FA, resend2FA } from '../../store/Auth';
import './style.css';

const maskPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  const last2 = digits.slice(-2);
  const masked = '+' + digits.slice(0, 1) + ' ' + '•'.repeat(4) + ' •'.repeat(2) + '••' + ' ' + last2;
  return masked;
};

const SMSOTPVerification = ({ userId, phone, onBack, history }) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(300);
  const inputRefs = useRef([]);
  const timerIntervalRef = useRef(null);

  const startTimer = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    setTimer(300);
    timerIntervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 0) { clearInterval(timerIntervalRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (inputRefs.current[0]) inputRefs.current[0].focus();
    startTimer();
    return () => clearInterval(timerIntervalRef.current);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newOtp.every(d => d !== '')) handleVerify(newOtp.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtp(newOtp);
      const lastFilledIndex = Math.min(pastedData.length - 1, 5);
      inputRefs.current[lastFilledIndex]?.focus();
      if (pastedData.length === 6) handleVerify(pastedData);
    }
  };

  const handleVerify = (otpCode = otp.join('')) => {
    if (otpCode.length !== 6) { EventBus.publish('error', 'Please enter all 6 digits'); return; }
    if (timer <= 0) { EventBus.publish('error', 'Code has expired. Please request a new one.'); return; }
    setIsLoading(true);
    dispatch(verify2FA({
      userId,
      otpCode,
      history,
      successCallback: () => setIsLoading(false),
      failCallback: () => {
        setIsLoading(false);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    }));
  };

  const handleResend = () => {
    setIsResending(true);
    dispatch(resend2FA({
      userId,
      successCallback: () => {
        setIsResending(false);
        setOtp(['', '', '', '', '', '']);
        startTimer();
        inputRefs.current[0]?.focus();
      },
      failCallback: () => setIsResending(false),
    }));
  };

  return (
    <div className='sms-otp-wrapper'>
      <h3>Verify your identity</h3>
      <p className='sms-otp-description'>
        We sent a 6-digit code to your mobile number.<br />
        Enter it below to continue signing in.
      </p>

      <div className='sms-phone-display'>
        <span className='sms-phone-icon'>📱</span>
        <span>{maskPhone(phone)}</span>
      </div>

      <div className='sms-otp-inputs'>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type='text'
            inputMode='numeric'
            maxLength='1'
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={isLoading}
            className={`sms-otp-input${digit ? ' filled' : ''}`}
            autoComplete='off'
          />
        ))}
      </div>

      {timer > 0 ? (
        <p className='sms-otp-timer'>Expires in {formatTime(timer)}</p>
      ) : (
        <p className='sms-otp-timer expired'>Code has expired</p>
      )}

      <div className='sms-otp-links'>
        <button onClick={handleResend} disabled={isLoading || isResending} className='sms-link-btn'>
          {isResending ? 'Sending...' : "Didn't receive it? Resend code"}
        </button>
        <button onClick={onBack} disabled={isLoading} className='sms-link-btn back'>
          ← Back to sign in
        </button>
      </div>
    </div>
  );
};

export default SMSOTPVerification;
